/**
 * Cadence in-house analytics client.
 * Fires pageviews on route change + named events on demand.
 * Anonymous by default; if a JWT is set in localStorage it's forwarded so
 * the server can link the event to the user_id/email.
 *
 * Small, no dependencies, ~1KB minified.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const ANON_KEY = "cadence_anon_id";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function getAnonId() {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
        id =
            (crypto.randomUUID && crypto.randomUUID()) ||
            "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(ANON_KEY, id);
    }
    return id;
}

function readUtms() {
    try {
        const q = new URLSearchParams(window.location.search);
        return {
            utm_source: q.get("utm_source") || undefined,
            utm_medium: q.get("utm_medium") || undefined,
            utm_campaign: q.get("utm_campaign") || undefined,
        };
    } catch {
        return {};
    }
}

async function send(body) {
    try {
        const token = localStorage.getItem("vf_token");
        await fetch(`${BACKEND}/api/track`, {
            method: "POST",
            keepalive: true, // survives page unload
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
        });
    } catch {
        // never surface to users
    }
}

export function trackPageview(path) {
    // Skip admin pages — no need to inflate our own numbers.
    if (path && (path.startsWith("/admin") || path.startsWith("/brand"))) return;
    send({
        type: "pageview",
        path: path || window.location.pathname,
        referrer: document.referrer || "",
        anon_id: getAnonId(),
        screen_w: window.innerWidth,
        screen_h: window.innerHeight,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...readUtms(),
    });
}

export function trackEvent(name, meta) {
    send({
        type: "event",
        name,
        path: window.location.pathname,
        referrer: document.referrer || "",
        anon_id: getAnonId(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        meta: meta || {},
        ...readUtms(),
    });
}

/**
 * Drop this component once inside <BrowserRouter> to auto-emit pageviews.
 */
export function AnalyticsRouter() {
    const loc = useLocation();
    useEffect(() => {
        trackPageview(loc.pathname + loc.search);
    }, [loc.pathname, loc.search]);
    return null;
}
